import { print } from "graphql";
import { PropertyValueSet } from "@promaster-sdk/property";
import { Carousel } from "@mantine/carousel";
import AutoScroll from "embla-carousel-auto-scroll";
import { useMediaQuery } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { IconX } from "@tabler/icons-react";
import { Fragment, useContext, useMemo, useRef, useState } from "react";
import { ActionIcon, Alert, Box, Button, Center, Container, Image, Loader, SimpleGrid, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { TOOLKIT_PRODUCT_ID } from "../config/toolkit-config";
import { LanguageContext } from "../contexts/app-context";
import { Tools } from "../generated/generated-operations";
import { requestGraphql } from "../queries/graphql-client";
import { allToolsQuery, productCarouselImagesQuery } from "../queries/queries";
import { ToolCard } from "./tool-card";
import { useTranslation } from "../hooks/translation-hook";
import { texts } from "../texts/texts";
import { isVisibleFromPropertyFilter, matchesPropertyFilter } from "../queries/visibility-filter";

const IMAGE_BASE = "https://promaster-publish.panasonic.divid.se/files/f83bb8f8-030a-4df2-ba4e-5ad57aa41ff1/blob_";

type AllToolsResponse = {
  readonly product?: {
    readonly modules?: {
      readonly custom_tables?: {
        readonly tools?: ReadonlyArray<Tools>;
      };
    };
  } | null;
};

type ProductCarouselImagesResponse = {
  readonly product?: {
    readonly name?: string;
    readonly modules?: {
      readonly properties?: {
        readonly property?: ReadonlyArray<{
          readonly name?: string | null;
          readonly values?: ReadonlyArray<ToolCarouselPropertyValue>;
        }>;
      };
    };
  } | null;
};

type ToolCarouselPropertyValue = {
  readonly property_filter?: string | null;
  readonly image?: string | null;
};

type FetchedToolsData = {
  readonly tools: ReadonlyArray<Tools>;
};

type FetchedCarouselImagesData = {
  readonly productName?: string;
  readonly carouselPropertyValues: ReadonlyArray<ToolCarouselPropertyValue>;
};

type ToolCategoryGroup = {
  readonly key: string;
  readonly tools: ReadonlyArray<Tools>;
};

function sortTools(tools: ReadonlyArray<Tools>): ReadonlyArray<Tools> {
  return [...tools].sort((a, b) => {
    const aSortNo = a.sort_no ?? Number.POSITIVE_INFINITY;
    const bSortNo = b.sort_no ?? Number.POSITIVE_INFINITY;
    if (aSortNo !== bSortNo) return aSortNo - bSortNo;
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
}

function buildImageUrl(blobId: string): string {
  return `${IMAGE_BASE}${blobId}`;
}

function getToolKey(tool: Tools): string {
  return `${tool.builtin_id ?? tool.name ?? "tool"}-${tool.sort_no ?? 0}`;
}

function getToolCategoryKey(tool: Tools): string {
  const category = tool.category?.trim().toLowerCase();
  return category && category.length > 0 ? category : "commercial";
}

function groupToolsByCategory(tools: ReadonlyArray<Tools>): ReadonlyArray<ToolCategoryGroup> {
  const groupsByCategory = new Map<string, Tools[]>();

  for (const tool of tools) {
    const categoryKey = getToolCategoryKey(tool);
    const categoryTools = groupsByCategory.get(categoryKey);

    if (categoryTools) {
      categoryTools.push(tool);
      continue;
    }

    groupsByCategory.set(categoryKey, [tool]);
  }

  return Array.from(groupsByCategory, ([key, categoryTools]) => ({ key, tools: categoryTools }));
}

function humanizeCategoryKey(categoryKey: string): string {
  return categoryKey
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function isPlaceholderTranslation(value: string, key: string): boolean {
  return /^\{[^.]+\.[^}]+\}$/.test(value.trim()) && value.includes(`.${key}}`);
}

function getCategorySectionId(categoryKey: string): string {
  if (categoryKey === "residential" || categoryKey === "commercial") {
    return categoryKey;
  }

  return `category-${categoryKey.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "tools"}`;
}

function getCarouselPropertyValues(response: ProductCarouselImagesResponse): ReadonlyArray<ToolCarouselPropertyValue> {
  const properties = response.product?.modules?.properties?.property;
  if (!Array.isArray(properties)) {
    return [];
  }

  const carouselProperty = properties.find((property) => property.name?.trim().toLowerCase() === "carousel_images");
  return Array.isArray(carouselProperty?.values) ? carouselProperty.values : [];
}

function buildToolFilterValues(toolName: string | undefined, productName: string | undefined): PropertyValueSet.PropertyValueSet {
  let filterValues = PropertyValueSet.Empty;

  const normalizedToolName = toolName?.trim();
  if (normalizedToolName) {
    filterValues = PropertyValueSet.setText("name", normalizedToolName, filterValues);
    filterValues = PropertyValueSet.setText("tool_name", normalizedToolName, filterValues);
  }

  const normalizedProductName = productName?.trim();
  if (normalizedProductName) {
    filterValues = PropertyValueSet.setText("product_name", normalizedProductName, filterValues);
  }

  return filterValues;
}

function resolveCarouselImagesForTool(
  toolName: string | undefined,
  productName: string | undefined,
  carouselPropertyValues: ReadonlyArray<ToolCarouselPropertyValue>
): ReadonlyArray<string> {
  if (!toolName || carouselPropertyValues.length === 0) {
    return [];
  }

  const filterValues = buildToolFilterValues(toolName, productName);
  const seenImages = new Set<string>();
  const imageUrls: string[] = [];

  for (const value of carouselPropertyValues) {
    const imageId = value.image?.trim();
    if (!imageId) {
      continue;
    }

    if (!matchesPropertyFilter(value.property_filter, filterValues)) {
      continue;
    }

    if (seenImages.has(imageId)) {
      continue;
    }

    seenImages.add(imageId);
    imageUrls.push(buildImageUrl(imageId));
  }

  return imageUrls;
}

function InlineToolCarousel({
  carouselImages,
  onClose,
  toolColor,
  toolName,
}: {
  readonly carouselImages: ReadonlyArray<string>;
  readonly onClose: () => void;
  readonly toolColor: string;
  readonly toolName: string;
}): JSX.Element {
  const isSingleImage = carouselImages.length === 1;
  const multiImageSlideSize =
    carouselImages.length === 2
      ? { base: "92%", sm: "74%", md: "58%" }
      : { base: "90%", sm: "66%", md: "46%" };
  const loopingImages =
    carouselImages.length >= 4
      ? carouselImages
      : Array.from({ length: 4 }, (_, index) => carouselImages[index % carouselImages.length]);
  const autoScroll = useRef(
    AutoScroll({
      speed: 1.1,
      startDelay: 0,
      stopOnInteraction: false,
      stopOnMouseEnter: false,
      playOnInit: true,
    })
  );

  return (
    <Box
      aria-label={toolName}
      style={{
        gridColumn: "1 / -1",
        width: "100vw",
        maxWidth: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        background: toolColor,
        borderRadius: 0,
        boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.18)",
        overflow: "hidden",
        position: "relative",
        transformOrigin: "top",
        animation: "toolkit-carousel-reveal 220ms ease both",
      }}
    >
      <ActionIcon
        aria-label={`Close ${toolName} carousel`}
        color="white"
        onClick={onClose}
        radius="xl"
        size="lg"
        style={{
          position: "absolute",
          right: "clamp(0.75rem, 2vw, 1.5rem)",
          top: "clamp(0.75rem, 2vw, 1.5rem)",
          zIndex: 2,
          backgroundColor: "rgba(0, 0, 0, 0.22)",
          border: "1px solid rgba(255, 255, 255, 0.35)",
        }}
        variant="filled"
      >
        <IconX size={18} />
      </ActionIcon>

      <style>
        {`@keyframes toolkit-carousel-reveal { from { opacity: 0; transform: translateY(-10px) scaleY(0.98); } to { opacity: 1; transform: translateY(0) scaleY(1); } }`}
      </style>
      {isSingleImage ? (
        <Box
          style={{
            height: "clamp(300px, 42vw, 540px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "clamp(0.75rem, 2vw, 1.5rem)",
          }}
        >
          <Box
            component="img"
            src={carouselImages[0]}
            alt={`${toolName} screenshot 1`}
            style={{
              display: "block",
              maxHeight: "100%",
              maxWidth: "min(100%, 1100px)",
              objectFit: "contain",
            }}
          />
        </Box>
      ) : (
        <Carousel
          emblaOptions={{ loop: true, align: "start", dragFree: true, containScroll: false }}
          height="clamp(300px, 42vw, 540px)"
          slideSize={multiImageSlideSize}
          slideGap={{ base: "xs", md: "sm" }} // Reduce spacing here
          plugins={[autoScroll.current]}
        >
          {loopingImages.map((src, i) => (
            <Carousel.Slide key={`${src}-${i}`}>
              <Box
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
              >
                <Image
                  src={src}
                  alt={`${toolName} screenshot ${i + 1}`}
                  style={{
                    display: "block",
                    maxHeight: "75%",
                    maxWidth: "75%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Carousel.Slide>
          ))}
        </Carousel>
      )}
    </Box>
  );
}

async function fetchTools(productId: string): Promise<FetchedToolsData> {
  const data = await requestGraphql<AllToolsResponse, { productId: string }>(print(allToolsQuery), { productId });
  const tools = data.product?.modules?.custom_tables?.tools;

  if (!Array.isArray(tools)) {
    throw new Error("Error");
  }

  return { tools };
}

async function fetchProductCarouselImages(productId: string): Promise<FetchedCarouselImagesData> {
  const data = await requestGraphql<ProductCarouselImagesResponse, { productId: string }>(
    print(productCarouselImagesQuery),
    { productId }
  );

  return {
    productName: data.product?.name,
    carouselPropertyValues: getCarouselPropertyValues(data),
  };
}

export function ToolsSection(): JSX.Element {
  const translate = useTranslation();
  const language = useContext(LanguageContext);
  const theme = useMantineTheme();
  const isXs = useMediaQuery("(min-width: 36em)");
  const isMd = useMediaQuery("(min-width: 62em)");
  const [expandedToolId, setExpandedToolId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["tools", language, TOOLKIT_PRODUCT_ID],
    queryFn: () => fetchTools(TOOLKIT_PRODUCT_ID),
  });

  const carouselImagesQuery = useQuery({
    queryKey: ["product-carousel-images", language, TOOLKIT_PRODUCT_ID],
    queryFn: () => fetchProductCarouselImages(TOOLKIT_PRODUCT_ID),
  });

  const tools = useMemo(
    () => sortTools((query.data?.tools ?? []).filter((tool) => isVisibleFromPropertyFilter(tool.visible))),
    [query.data]
  );

  const carouselImagesByToolKey = useMemo(() => {
    const propertyValues = carouselImagesQuery.data?.carouselPropertyValues ?? [];
    const productName = carouselImagesQuery.data?.productName;
    const byToolKey = new Map<string, ReadonlyArray<string>>();

    for (const tool of tools) {
      byToolKey.set(getToolKey(tool), resolveCarouselImagesForTool(tool.name ?? undefined, productName, propertyValues));
    }

    return byToolKey;
  }, [carouselImagesQuery.data?.carouselPropertyValues, carouselImagesQuery.data?.productName, tools]);

  const toolCategoryGroups = useMemo(() => groupToolsByCategory(tools), [tools]);

  const columnCount = isXs ? 2 : 1;

  function getCategoryTitle(categoryKey: string): string {
    const translatedCategory =
      categoryKey === "residential"
        ? translate(texts.residential)
        : categoryKey === "commercial"
          ? translate(texts.commercial)
          : translate(categoryKey, "toolkit");

    return translatedCategory && !isPlaceholderTranslation(translatedCategory, categoryKey)
      ? translatedCategory
      : humanizeCategoryKey(categoryKey);
  }

  function renderToolGrid(toolsToRender: ReadonlyArray<Tools>, columnCount: number): JSX.Element[] {
    const expandedIndex = toolsToRender.findIndex((tool) => getToolKey(tool) === expandedToolId);
    const expandedRowIndex = expandedIndex >= 0 ? Math.floor(expandedIndex / columnCount) : -1;

    return toolsToRender.map((tool, index) => {
      const toolKey = getToolKey(tool);
      const carouselImages = carouselImagesByToolKey.get(toolKey) ?? [];
      const isCarouselOpen = expandedToolId === toolKey;
      const currentRowIndex = Math.floor(index / columnCount);
      const isLastItemInRow = index % columnCount === columnCount - 1;
      const isLastItem = index === toolsToRender.length - 1;
      const shouldRenderCarousel = expandedRowIndex === currentRowIndex && (isLastItemInRow || isLastItem);
      const expandedTool = expandedIndex >= 0 ? toolsToRender[expandedIndex] : undefined;
      const expandedImages = expandedTool ? carouselImagesByToolKey.get(getToolKey(expandedTool)) ?? [] : [];
      const expandedColor = expandedTool?.primaryColor ?? theme.colors[theme.primaryColor]?.[7] ?? "#6f9f76";
      const showTakeALookAction = !carouselImagesQuery.isFetched || carouselImages.length > 0;

      function handleToggleCarousel(): void {
        setExpandedToolId(isCarouselOpen ? null : toolKey);
      }

      return (
        <Fragment key={toolKey}>
          <ToolCard
            tool={tool}
            carouselImages={carouselImages}
            showTakeALookAction={showTakeALookAction}
            isCarouselOpen={isCarouselOpen}
            onToggleCarousel={handleToggleCarousel}
          />
          {shouldRenderCarousel && expandedTool && expandedImages.length > 0 && (
            <InlineToolCarousel
              toolName={expandedTool.name ?? "Toolkit tool"}
              toolColor={expandedColor}
              carouselImages={expandedImages}
              onClose={() => setExpandedToolId(null)}
            />
          )}
        </Fragment>
      );
    });
  }

  if (query.isLoading) {
    return (
      <Box bg="#f1f3f5" py="4rem">
        <Center>
          <Loader />
        </Center>
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Box bg="#f1f3f5" py="4rem">
        <Container size="xl">
          <Stack gap="md">
            <Alert color="red" title="Unable to load tools" variant="light">
              {query.error instanceof Error ? query.error.message : "An unexpected error happened while loading tools."}
            </Alert>
            <Button onClick={() => query.refetch()} w={{ base: "100%", sm: "fit-content" }}>
              {translate(texts.retry)}
            </Button>
          </Stack>
        </Container>
      </Box>
    );
  }

  if (tools.length === 0) {
    return (
      <Box bg="#f1f3f5" py="4rem">
        <Container size="xl">
          <Stack gap="xs" align="center">
            <Title order={4}>{translate(texts.no_tools_available)}</Title>
            <Text size="sm" c="dimmed" ta="center">
              {translate(texts.no_tools_found)}
            </Text>
            <Button variant="light" onClick={() => query.refetch()}>
              {translate(texts.retry)}
            </Button>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="#f1f3f5" py={{ base: "2rem", md: "3rem" }}>
      <Container size="xl">
        <Stack gap="3rem">
          {toolCategoryGroups.map((categoryGroup) => (
            <Box key={categoryGroup.key} id={getCategorySectionId(categoryGroup.key)}>
              <Stack gap="lg">
                <Title order={3} fw={600}>
                  {getCategoryTitle(categoryGroup.key)}
                </Title>
                <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="lg">
                  {renderToolGrid(categoryGroup.tools, columnCount)}
                </SimpleGrid>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
